import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACK_KEY = "mori_ack_version";

// Detects a new deployed version and prompts the user to reload — once.
// Hardened against transient/replica flip-flops: a changed version must be
// confirmed on two consecutive polls, and an acknowledged version is persisted
// so a reload never re-triggers the prompt.
export default function VersionWatcher() {
  const baseline = useRef(null);
  const candidate = useRef(null);
  const notified = useRef(false);

  useEffect(() => {
    let timer;
    const check = async () => {
      try {
        const { data } = await api.get("/version");
        const v = data?.version;
        if (!v) return;

        if (baseline.current == null) {
          baseline.current = v;
          return;
        }
        if (v === baseline.current || notified.current) {
          candidate.current = null;
          return;
        }
        const ack = (() => { try { return localStorage.getItem(ACK_KEY); } catch { return null; } })();
        if (v === ack) {
          baseline.current = v; // already acknowledged this version; don't nag
          return;
        }
        // Require the same new version on two consecutive polls before prompting.
        if (candidate.current !== v) {
          candidate.current = v;
          return;
        }
        notified.current = true;
        toast("Actualizar versión", {
          description: "Hay una nueva versión disponible de Mori.",
          duration: Infinity,
          action: {
            label: "Actualizar",
            onClick: () => {
              try { localStorage.setItem(ACK_KEY, v); } catch { /* ignore */ }
              window.location.reload();
            },
          },
        });
      } catch {
        /* ignore transient errors */
      }
    };
    check();
    timer = setInterval(check, 60000);
    return () => clearInterval(timer);
  }, []);

  return null;
}
